import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "./themeupdate.css";
import "../rules/rules.css";
import "./gameContent.css";

import { fetchThemeData } from "../../themeSlice";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../sessionSlice";
import {
  listQuestions,
  saveQuestion,
  deleteQuestion,
  importQuestionsCsv,
} from "../../functions/questionsApi";
import {
  listParticipants,
  saveParticipant,
  deleteParticipant,
  importParticipantsCsv,
} from "../../functions/participantsApi";
import GamePageImageTab from "./GamePageImageTab";
import QuestionsTab from "./QuestionsTab";
import ParticipantsTab from "./ParticipantsTab";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";

/**
 * Tab ids keep the #tab2 / #tab3 / #tab4 hashes of old/admin/themeUpdate.php, which links
 * "Add Questions" to editor.php and "Add Participants" to participants.php.
 */
const TABS = [
  { id: "tab2", label: "Add Game Page Image" },
  { id: "tab3", label: "Add Questions" },
  { id: "tab4", label: "Add Participants" },
];

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const hashTab = location.hash.replace("#", "");
  const activeTab = TABS.some((t) => t.id === hashTab) ? hashTab : "tab2";

  const [questions, setQuestions] = useState(null); // null = not loaded yet
  const [participants, setParticipants] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  const reloadTheme = useCallback(() => {
    if (!currentTheme) return Promise.resolve();
    return dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    reloadTheme();
  }, [reloadTheme]);

  const reloadQuestions = useCallback(async () => {
    const rows = await listQuestions({
      themeName: currentTheme,
      organizationId: organizationId || "admin",
      sessionId: sessionId || "admin",
      token: adminToken,
    });
    setQuestions(rows);
  }, [currentTheme, adminToken, organizationId, sessionId]);

  const reloadParticipants = useCallback(async () => {
    setParticipants(await listParticipants({ themeName: currentTheme, token: adminToken }));
  }, [currentTheme, adminToken]);

  // Each list loads when its tab opens, like editor.php / participants.php loading their own rows.
  useEffect(() => {
    if (!currentTheme || !adminToken || activeTab === "tab2") return;
    const reload = activeTab === "tab3" ? reloadQuestions : reloadParticipants;
    setIsLoadingList(true);
    reload()
      .catch((err) => Swal.fire({ icon: "error", title: "Error", text: err?.message }))
      .finally(() => setIsLoadingList(false));
  }, [activeTab, currentTheme, adminToken, reloadQuestions, reloadParticipants]);

  useEffect(() => {
    if (!data) return undefined;
    if (!shouldAutoStartThemeUpdateTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = useCallback(() => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  }, []);

  /** Runs one mutation, then reloads the list. Rethrows so modals stay open on error. */
  const runAction = async (action, reload, successTitle) => {
    setIsSaving(true);
    try {
      await action();
      await reload();
      Swal.fire({ icon: "success", title: successTitle, timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Something went wrong" });
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const questionHandlers = {
    onSaveQuestion: ({ action, editId, question }) =>
      runAction(
        () => saveQuestion({ themeName: currentTheme, token: adminToken, action, editId, question }),
        reloadQuestions,
        action === "edit" ? "Question Updated Successfully!" : "Question Added Successfully!"
      ),
    onDeleteQuestion: (id) =>
      runAction(
        () => deleteQuestion({ themeName: currentTheme, id, token: adminToken }),
        reloadQuestions,
        "Deleted Successfully!"
      ).catch(() => {}),
    onImportCsv: (rows) =>
      runAction(
        () => importQuestionsCsv({ themeName: currentTheme, rows, token: adminToken }),
        reloadQuestions,
        "Successfully Updated All Fields"
      ).catch(() => {}),
  };

  const participantHandlers = {
    onSaveParticipant: ({ action, editId, participant }) =>
      runAction(
        () =>
          saveParticipant({ themeName: currentTheme, token: adminToken, action, editId, participant }),
        reloadParticipants,
        action === "edit" ? "Updated Successfully!" : "Added Successfully!"
      ),
    onDeleteParticipant: (id) =>
      runAction(
        () => deleteParticipant({ themeName: currentTheme, id, token: adminToken }),
        reloadParticipants,
        "Deleted Successfully!"
      ).catch(() => {}),
    onImportCsv: (rows) =>
      runAction(
        () => importParticipantsCsv({ themeName: currentTheme, rows, token: adminToken }),
        reloadParticipants,
        "Successfully Updated All Fields"
      ).catch(() => {}),
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <nav className="se-tabs" data-tour="tour-gc-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`themeupdate-tab-outline se-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => navigate({ hash: `#${tab.id}` }, { replace: true })}
          >
            <i className="fa-solid fa-square-plus" aria-hidden /> {tab.label}
          </button>
        ))}
      </nav>

      <div data-tour="tour-gc-content">
        {activeTab === "tab2" && (
          <GamePageImageTab
            themeData={data}
            currentTheme={currentTheme}
            adminToken={adminToken}
            onUploaded={reloadTheme}
          />
        )}
        {activeTab === "tab3" && (
          <QuestionsTab
            questions={questions || []}
            isLoading={isLoadingList || questions === null}
            isSaving={isSaving}
            {...questionHandlers}
          />
        )}
        {activeTab === "tab4" && (
          <ParticipantsTab
            participants={participants || []}
            isLoading={isLoadingList || participants === null}
            isSaving={isSaving}
            {...participantHandlers}
          />
        )}
      </div>

      <ThemeUpdateFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default ThemeUpdate;
